import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBillingTaxV1TaxSettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/tax-settings',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/billing/v1/tax-settings',
        destPath: '/v1/tax-settings',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/tax-settings',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/tax-settings',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/tax-settings',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/tax-settings',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/tax-settings',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/tax-settings',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/tax-settings',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/tax-settings',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/tax-settings',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/tax-settings',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_tax-settings';

/**
 * Retrieves the site's tax settings.
 *
 * If the site's tax settings have never been set, default values are returned.
 */
export function getTaxSettings(payload: object): RequestOptionsFactory<any> {
  function __getTaxSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billing.tax.v1.tax_settings',
      method: 'GET' as any,
      methodFqn: 'wix.billing.tax.v1.TaxSettingsService.GetTaxSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingTaxV1TaxSettingsServiceUrl({
        protoPath: '/v1/tax-settings',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'taxSettings.createdDate' },
              { path: 'taxSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getTaxSettings;
}

/**
 * Creates or updates the site's tax settings.
 *
 * If the site's tax settings don't exist yet, this method creates them. Otherwise, it updates the existing settings.
 *
 * Since these settings are shared across all Wix products on the site, changes take effect immediately for all integrated products.
 */
export function upsertTaxSettings(payload: object): RequestOptionsFactory<any> {
  function __upsertTaxSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'taxSettings.createdDate' },
          { path: 'taxSettings.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.billing.tax.v1.tax_settings',
      method: 'POST' as any,
      methodFqn: 'wix.billing.tax.v1.TaxSettingsService.UpsertTaxSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillingTaxV1TaxSettingsServiceUrl({
        protoPath: '/v1/tax-settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'taxSettings.createdDate' },
              { path: 'taxSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __upsertTaxSettings;
}
